import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';

import {
  AutoscalerRebalancingPlan,
  RebalancingPlanStatusEnum,
} from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const EXTERNAL_CLUSTER_REBALANCE_PLAN_QUERY_KEY =
  'external-cluster-rebalance-plan';

type RebalancePlanQueryKeyParams = {
  clusterId?: string;
  rebalancePlanId?: string;
  includeOperations?: boolean;
  includeConfigurations?: boolean;
  tourStepId?: string | number | null;
};

export const getRebalancePlanQueryKey = ({
  clusterId,
  rebalancePlanId,
  includeOperations,
  includeConfigurations,
  tourStepId,
}: RebalancePlanQueryKeyParams = {}) =>
  [
    EXTERNAL_CLUSTER_REBALANCE_PLAN_QUERY_KEY,
    clusterId,
    rebalancePlanId,
    includeOperations,
    includeConfigurations,
    tourStepId,
  ] as const;

type RebalancePlansQueryOptionsParams = {
  clusterId?: string;
  limit?: number;
  includeStatuses?: RebalancingPlanStatusEnum[];
  includeOperations?: boolean;
  includeConfigurations?: boolean;
  includeNegative?: boolean;
  enabled?: boolean;
};

export const getRebalancePlansQueryKey = ({
  clusterId,
  limit = 20,
  includeStatuses,
  includeOperations = false,
  includeConfigurations = false,
  includeNegative = true,
}: Omit<RebalancePlansQueryOptionsParams, 'enabled'> = {}) =>
  [
    EXTERNAL_CLUSTER_REBALANCE_PLAN_QUERY_KEY,
    clusterId,
    limit,
    includeStatuses,
    includeOperations,
    includeConfigurations,
    includeNegative,
  ] as const;

export const getRebalancePlansQueryOptions = ({
  clusterId,
  limit = 20,
  includeStatuses,
  includeOperations = false,
  includeConfigurations = false,
  includeNegative = true,
  enabled,
}: RebalancePlansQueryOptionsParams = {}) =>
  infiniteQueryOptions({
    queryKey: getRebalancePlansQueryKey({
      clusterId,
      limit,
      includeStatuses,
      includeOperations,
      includeConfigurations,
      includeNegative,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.autoscaler.listRebalancingPlans({
        clusterId: clusterId!,
        limit: String(limit),
        cursor: pageParam,
        filterStatuses: includeStatuses,
        includeOperations,
        includeConfigurations,
        includeNegative,
      });
      return data;
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: !!clusterId && enabled !== false,
    refetchInterval: 20000,
  });

type UseRebalancePlansQuery = {
  isLoading: boolean;
  rebalancePlans?: AutoscalerRebalancingPlan[];
  activeRebalancingPlanId?: string;
  error?: unknown;
  refetch?: () => void;
  fetchNextPage?: () => void;
  isFetching?: boolean;
  hasNextPage?: boolean;
};

type UseRebalancePlansQueryProps = {
  clusterId?: string;
  disabled?: boolean;
  limit?: number;
  includeStatuses?: RebalancingPlanStatusEnum[];
  includeOperations?: boolean;
  includeConfigurations?: boolean;
  includeNegative?: boolean;
};

export const useRebalancePlansQuery = ({
  clusterId,
  disabled,
  limit = 20,
  includeStatuses,
  includeOperations = false,
  includeConfigurations = false,
  includeNegative = true,
}: UseRebalancePlansQueryProps): UseRebalancePlansQuery => {
  const {
    isLoading,
    data,
    error,
    refetch,
    fetchNextPage,
    isFetching,
    hasNextPage,
  } = useInfiniteQuery(
    getRebalancePlansQueryOptions({
      clusterId,
      limit,
      includeStatuses,
      includeOperations,
      includeConfigurations,
      includeNegative,
      enabled: !disabled,
    })
  );

  const pages = data?.pages;
  const rebalancePlans = pages ? pages.flatMap(({ items }) => items ?? []) : [];

  return {
    isLoading,
    rebalancePlans,
    error,
    refetch,
    fetchNextPage,
    isFetching,
    hasNextPage,
  };
};
